import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { ToolbarItem } from "@omegagrid/toolbar";
import { Token } from "@reporting/rp-types/auth";

const PAGE_SIZE = 500;

export class AccessTokenGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}],
			filter: false
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			w: 150,
		}, {
			data: 'accounting',
			header: '#',
			t: 't',
			w: 70,
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			w: 150,
		}, {
			data: 'group_wide',
			header: msg('Whole group'),
			t: 'b',
			w: 100,
		}, {
			data: 'user_name',
			header: msg('User'),
			t: 't',
			w: 150,
		}, {
			data: 'scope',
			header: msg('Scope'),
			t: 't',
			w: 150,
		}, {
			data: 'id_application',
			header: msg('App ID'),
			t: 'n',
			w: 80,
		}, {
			data: 'expiration',
			header: msg('Expiration'),
			t: 't',
			w: 180,
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			w: 145,
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const tokens = await rp.ms('auth').get<Token[]>(`/oauth2/tokens`, this.createRequestParams(filters, {
			count: PAGE_SIZE,
			fields: ['idz', 'name', 'accounting', 'company', 'scope_group', 'user_name', 'id_application', 'scope', 'expiration', 'date_created'],
		}));
		return tokens.map(data => ({
			data: {
				...data,
				group_wide: data.scope_group != null,
				scope: data.scope ? data.scope.split(/\s+/).join(', ') : null,
			}
		}));
	}

	async save(rowData: RowData[]) {
		// A refused revocation (a group-wide token is listed to every admin of the group, but only an
		// admin of its home company may revoke it) must not keep the other rows from going through.
		// The service has already shown its message, and the refresh after save lists the token again.
		for (const row of rowData) {
			if (row.state !== 'deleted') continue;
			const idz = row.data.idz as number;
			if (!idz) continue;
			try {
				await rp.ms('auth').delete(`/oauth2/token/${idz}`);
			} catch {
				// reported by the service
			}
		}
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			refreshAfterSave: true,
			toolbar: {
				new: {enabled: false},
			},
			customToolbar: [{
				type: 'button',
				id: 'addAccessToken',
				icon: 'plus',
				color: ColorTypes.Blue,
				text: msg('Access token'),
			}] as ToolbarItem[],
			rowMenu: {
				deleteRows: true,
			},
		});
	}

	async onAction(action: string, cell: CellModel): Promise<void> {
		switch (action) {
			case 'addAccessToken':
				rp.host.openModalSidebar({
					id: 'admin-accessTokenForm',
					caption: msg('New access token')
				});
				break;
		}
	}

}
